import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Eye, EyeOff, Check, X, MoreHorizontal, Trash2, KeyRound } from "lucide-react";
import {
  getPasswordStrength,
  getStrengthText,
  getStrengthColor,
} from "@/lib/password-utils";
import { useAdminAuth } from "@/context/Admin";
import Loader from "@/components/loader/loader";

export default function AdminAccountsTab() {
  const { registerAdmin, getAllAdmins, deleteAdmin, initiateAdminPasswordReset, admin } = useAdminAuth();
  const [showNewAdminPassword, setShowNewAdminPassword] = useState(false);
  const [showConfirmNewAdminPassword, setShowConfirmNewAdminPassword] =
    useState(false);
  const [newAdminName, setNewAdminName] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [confirmNewAdminPassword, setConfirmNewAdminPassword] = useState("");
  const [newAdminRole, setNewAdminRole] = useState("admin");
  const [adminCreationSuccess, setAdminCreationSuccess] = useState(false);
  const [adminCreationError, setAdminCreationError] = useState("");
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  
  // Admin list management
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [selectedAdminName, setSelectedAdminName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedAdminForReset, setSelectedAdminForReset] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);

  const adminPasswordStrength = getPasswordStrength(newAdminPassword);

  // Load admins on component mount
  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const data = await getAllAdmins(1, 100, "");
      setAdmins(data.admins || []);
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAdminCreationSuccess(false);
    setAdminCreationError("");
    setIsCreatingAdmin(true);

    if (newAdminPassword !== confirmNewAdminPassword) {
      setAdminCreationError("Passwords do not match.");
      setIsCreatingAdmin(false);
      return;
    }

    if (getPasswordStrength(newAdminPassword) < 75) {
      setAdminCreationError("Password is too weak.");
      setIsCreatingAdmin(false);
      return;
    }

    try {
      await registerAdmin(newAdminName, newAdminEmail, newAdminPassword, newAdminRole);
      setAdminCreationSuccess(true);
      setNewAdminName("");
      setNewAdminEmail("");
      setNewAdminPassword("");
      setConfirmNewAdminPassword("");
      setNewAdminRole("admin");
      // Refresh admin list
      await fetchAdmins();
    } catch (err) {
      setAdminCreationError(err.message);
    }

    setIsCreatingAdmin(false);
  };

  const handleDeleteClick = (adminItem) => {
    setSelectedAdminId(adminItem.id);
    setSelectedAdminName(adminItem.name);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAdminId) return;

    setDeleting(true);
    try {
      await deleteAdmin(selectedAdminId);
      await fetchAdmins(); // Refresh admin list
      setDeleteDialogOpen(false);
      setSelectedAdminId(null);
      setSelectedAdminName("");
    } catch (error) {
      console.error("Error deleting admin:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPasswordClick = (adminItem) => {
    setSelectedAdminForReset(adminItem);
    setResetPasswordDialogOpen(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!selectedAdminForReset) return;

    setResettingPassword(true);
    try {
      await initiateAdminPasswordReset(selectedAdminForReset.email);
      setResetPasswordDialogOpen(false);
      setSelectedAdminForReset(null);
    } catch (error) {
      console.error("Error resetting admin password:", error);
    } finally {
      setResettingPassword(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  // Restrict access to super admins only
  if (admin?.role !== "super_admin") {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p className="text-lg font-semibold text-gray-700 mb-2">Access Denied</p>
            <p className="text-sm text-gray-500">
              This section is only accessible to super administrators.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="text-primary">Create New Admin Account</CardTitle>
        <CardDescription>
          Add a new administrator to the system.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="newAdminName">Full Name</Label>
            <Input
              id="newAdminName"
              value={newAdminName}
              onChange={(e) => setNewAdminName(e.target.value)}
              placeholder="Enter full name"
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="newAdminEmail">Email Address</Label>
            <Input
              id="newAdminEmail"
              type="email"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value)}
              placeholder="Enter email address"
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="newAdminRole">Role</Label>
            <Select value={newAdminRole} onValueChange={setNewAdminRole}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="newAdminPassword">Password</Label>
            <div className="relative mt-1">
              <Input
                id="newAdminPassword"
                type={showNewAdminPassword ? "text" : "password"}
                placeholder="Create password"
                className="pr-10"
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowNewAdminPassword(!showNewAdminPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewAdminPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {newAdminPassword && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(
                        adminPasswordStrength
                      )}`}
                      style={{ width: `${adminPasswordStrength}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-600">
                    {getStrengthText(adminPasswordStrength)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <div className="flex items-center gap-2">
                    {newAdminPassword.length >= 8 ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-gray-400" />
                    )}
                    <span>At least 8 characters</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/[A-Z]/.test(newAdminPassword) ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-gray-400" />
                    )}
                    <span>One uppercase letter</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/[0-9]/.test(newAdminPassword) ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <X className="w-3 h-3 text-gray-400" />
                    )}
                    <span>One number</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="confirmNewAdminPassword">Confirm Password</Label>
            <div className="relative mt-1">
              <Input
                id="confirmNewAdminPassword"
                type={showConfirmNewAdminPassword ? "text" : "password"}
                placeholder="Confirm password"
                className="pr-10"
                value={confirmNewAdminPassword}
                onChange={(e) => setConfirmNewAdminPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() =>
                  setShowConfirmNewAdminPassword(!showConfirmNewAdminPassword)
                }
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmNewAdminPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {confirmNewAdminPassword &&
              newAdminPassword !== confirmNewAdminPassword && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <X className="w-3 h-3" />
                  Passwords do not match
                </p>
              )}
            {confirmNewAdminPassword &&
              newAdminPassword === confirmNewAdminPassword && (
                <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Passwords match
                </p>
              )}
          </div>
          <Button
            type="submit"
            disabled={
              isCreatingAdmin ||
              newAdminPassword !== confirmNewAdminPassword ||
              getPasswordStrength(newAdminPassword) < 75
            }
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {isCreatingAdmin ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Creating Admin...
              </div>
            ) : (
              "Create Admin Account"
            )}
          </Button>
          {/* {adminCreationSuccess && (
            <div className="mt-4 text-green-600 text-sm flex items-center gap-2">
              <Check className="w-4 h-4" /> Admin account created successfully!
            </div>
          )}
          {adminCreationError && (
            <div className="mt-4 text-red-600 text-sm flex items-center gap-2">
              <X className="w-4 h-4" /> {adminCreationError}
            </div>
          )} */}
        </form>
      </CardContent>
    </Card>

    {/* Admin List */}
    <Card className="mt-8">
      <CardHeader>
        <CardTitle className="text-primary">All Admins</CardTitle>
        <CardDescription>
          View and manage all administrator accounts in the system.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadingAdmins ? (
          <Loader />
        ) : admins.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((adminItem) => (
                <TableRow key={adminItem.id}>
                  <TableCell className="font-medium">{adminItem.name}</TableCell>
                  <TableCell>{adminItem.email}</TableCell>
                  <TableCell>
                    <Badge variant={adminItem.role === "super_admin" ? "default" : "outline"}>
                      {adminItem.role === "super_admin" ? "Super Admin" : "Admin"}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(adminItem.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {adminItem.id !== admin?.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleResetPasswordClick(adminItem)}
                            className="text-blue-600"
                          >
                            <KeyRound className="mr-2 h-4 w-4" />
                            Reset Password
                          </DropdownMenuItem>
                          {adminItem.role !== "super_admin" && (
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(adminItem)}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {adminItem.id === admin?.id && (
                      <span className="text-xs text-gray-500">Current User</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-center text-gray-500 py-4">No admins found.</p>
        )}
      </CardContent>
    </Card>

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete the admin account "{selectedAdminName}". This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmDelete}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleting ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Reset Password Confirmation Dialog */}
    <AlertDialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset Admin Password</AlertDialogTitle>
          <AlertDialogDescription>
            This will send a password reset OTP to {selectedAdminForReset?.email}. The admin will need to complete the reset process using the OTP sent to their email.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={resettingPassword}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmResetPassword}
            disabled={resettingPassword}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {resettingPassword ? "Sending Reset..." : "Send Reset Email"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
